'use client';

import { useTranslations } from 'next-intl';
import { LegalPageLayout } from '@/components/shared/legal-page-layout';

export default function PrivacyPolicy() {
  const t = useTranslations('pages.privacy');
  const tableOfContents = t.raw('toc') as Array<{ id: string; title: string; level: number }>;
  const personalItems = t.raw('sections.information.personal.items') as string[];
  const usageItems = t.raw('sections.information.usage.items') as string[];
  const useInfoItems = t.raw('sections.useInfo.items') as string[];
  const consentItems = t.raw('sections.legalBasis.consent.items') as string[];
  const contractItems = t.raw('sections.legalBasis.contract.items') as string[];
  const legitimateItems = t.raw('sections.legalBasis.legitimate.items') as string[];
  const dataSharingItems = t.raw('sections.dataSharing.items') as Array<{
    label: string;
    body: string;
  }>;
  const retentionItems = t.raw('sections.dataRetention.items') as Array<{
    label: string;
    body: string;
  }>;
  const transfersItems = t.raw('sections.internationalTransfers.items') as Array<{
    label: string;
    body: string;
  }>;
  const dpoItems = t.raw('sections.dataProtectionOfficer.items') as string[];
  const ageVerificationItems = t.raw('sections.childrensPrivacy.ageVerification.items') as string[];
  const parentalConsentItems = t.raw('sections.childrensPrivacy.parentalConsent.items') as string[];
  const breachItems = t.raw('sections.dataBreach.items') as string[];
  const contactItems = t.raw('sections.contact.items') as Array<{ label: string; value: string }>;
  return (
    <LegalPageLayout
      description={t('description')}
      lastUpdated={t('lastUpdated')}
      tableOfContents={tableOfContents}
      title={t('title')}
    >
      <div className="space-y-8">
        <section id="introduction">
          <h2 className="mb-4 font-display font-semibold text-2xl text-white">
            {t('sections.introduction.title')}
          </h2>
          <p className="mb-4 text-purple-200/80">{t('sections.introduction.body1')}</p>
          <p className="text-purple-200/80">{t('sections.introduction.body2')}</p>
        </section>

        <section id="information-we-collect">
          <h2 className="mb-4 font-display font-semibold text-2xl text-white">
            {t('sections.information.title')}
          </h2>

          <div className="mb-6" id="personal-information">
            <h3 className="mb-3 font-body font-semibold text-purple-100 text-xl">
              {t('sections.information.personal.title')}
            </h3>
            <p className="mb-4 text-purple-200/80">{t('sections.information.personal.intro')}</p>
            <ul className="mb-4 ml-6 list-disc space-y-2 text-purple-200/80">
              {personalItems.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </div>

          <div className="mb-6" id="usage-data">
            <h3 className="mb-3 font-body font-semibold text-purple-100 text-xl">
              {t('sections.information.usage.title')}
            </h3>
            <p className="mb-4 text-purple-200/80">{t('sections.information.usage.intro')}</p>
            <ul className="mb-4 ml-6 list-disc space-y-2 text-purple-200/80">
              {usageItems.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </div>

          <div className="mb-6" id="cookies-and-tracking">
            <h3 className="mb-3 font-body font-semibold text-purple-100 text-xl">
              {t('sections.information.cookies.title')}
            </h3>
            <p className="mb-4 text-purple-200/80">{t('sections.information.cookies.body')}</p>
          </div>
        </section>

        <section id="how-we-use-your-information">
          <h2 className="mb-4 font-display font-semibold text-2xl text-white">
            {t('sections.useInfo.title')}
          </h2>
          <p className="mb-4 text-purple-200/80">{t('sections.useInfo.intro')}</p>
          <ul className="mb-4 ml-6 list-disc space-y-2 text-purple-200/80">
            {useInfoItems.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </section>

        <section id="legal-basis">
          <h2 className="mb-4 font-display font-semibold text-2xl text-white">
            {t('sections.legalBasis.title')}
          </h2>
          <p className="mb-4 text-purple-200/80">{t('sections.legalBasis.intro')}</p>

          <div className="mb-6" id="consent">
            <h3 className="mb-3 font-body font-semibold text-purple-100 text-xl">
              {t('sections.legalBasis.consent.title')}
            </h3>
            <p className="mb-4 text-purple-200/80">{t('sections.legalBasis.consent.intro')}</p>
            <ul className="mb-4 ml-6 list-disc space-y-2 text-purple-200/80">
              {consentItems.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
            <p className="mb-4 text-purple-200/80">{t('sections.legalBasis.consent.note')}</p>
          </div>

          <div className="mb-6" id="contractual-necessity">
            <h3 className="mb-3 font-body font-semibold text-purple-100 text-xl">
              {t('sections.legalBasis.contract.title')}
            </h3>
            <p className="mb-4 text-purple-200/80">{t('sections.legalBasis.contract.intro')}</p>
            <ul className="mb-4 ml-6 list-disc space-y-2 text-purple-200/80">
              {contractItems.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </div>

          <div className="mb-6" id="legitimate-interests">
            <h3 className="mb-3 font-body font-semibold text-purple-100 text-xl">
              {t('sections.legalBasis.legitimate.title')}
            </h3>
            <p className="mb-4 text-purple-200/80">{t('sections.legalBasis.legitimate.intro')}</p>
            <ul className="mb-4 ml-6 list-disc space-y-2 text-purple-200/80">
              {legitimateItems.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </div>
        </section>

        <section id="data-sharing-and-disclosure">
          <h2 className="mb-4 font-display font-semibold text-2xl text-white">
            {t('sections.dataSharing.title')}
          </h2>
          <p className="mb-4 text-purple-200/80">{t('sections.dataSharing.intro')}</p>
          <ul className="mb-4 ml-6 list-disc space-y-2 text-purple-200/80">
            {dataSharingItems.map((item) => (
              <li key={item.label}>
                <strong>{item.label}</strong> {item.body}
              </li>
            ))}
          </ul>
        </section>

        <section id="data-security">
          <h2 className="mb-4 font-display font-semibold text-2xl text-white">
            {t('sections.dataSecurity.title')}
          </h2>
          <p className="mb-4 text-purple-200/80">{t('sections.dataSecurity.body')}</p>
        </section>

        <section id="data-retention">
          <h2 className="mb-4 font-display font-semibold text-2xl text-white">
            {t('sections.dataRetention.title')}
          </h2>
          <p className="mb-4 text-purple-200/80">{t('sections.dataRetention.intro')}</p>
          <ul className="mb-4 ml-6 list-disc space-y-2 text-purple-200/80">
            {retentionItems.map((item) => (
              <li key={item.label}>
                <strong>{item.label}</strong> {item.body}
              </li>
            ))}
          </ul>
          <p className="mb-4 text-purple-200/80">{t('sections.dataRetention.note')}</p>
        </section>

        <section id="your-gdpr-rights">
          <h2 className="mb-4 font-display font-semibold text-2xl text-white">
            {t('sections.gdprRights.title')}
          </h2>
          <p className="mb-4 text-purple-200/80">{t('sections.gdprRights.intro')}</p>

          <div className="mb-6" id="right-to-access">
            <h3 className="mb-3 font-body font-semibold text-purple-100 text-xl">
              {t('sections.gdprRights.access.title')}
            </h3>
            <p className="mb-4 text-purple-200/80">{t('sections.gdprRights.access.body')}</p>
          </div>

          <div className="mb-6" id="right-to-rectification">
            <h3 className="mb-3 font-body font-semibold text-purple-100 text-xl">
              {t('sections.gdprRights.rectification.title')}
            </h3>
            <p className="mb-4 text-purple-200/80">{t('sections.gdprRights.rectification.body')}</p>
          </div>

          <div className="mb-6" id="right-to-erasure">
            <h3 className="mb-3 font-body font-semibold text-purple-100 text-xl">
              {t('sections.gdprRights.erasure.title')}
            </h3>
            <p className="mb-4 text-purple-200/80">{t('sections.gdprRights.erasure.body')}</p>
          </div>

          <div className="mb-6" id="right-to-portability">
            <h3 className="mb-3 font-body font-semibold text-purple-100 text-xl">
              {t('sections.gdprRights.portability.title')}
            </h3>
            <p className="mb-4 text-purple-200/80">{t('sections.gdprRights.portability.body')}</p>
          </div>

          <div className="mb-6" id="right-to-object">
            <h3 className="mb-3 font-body font-semibold text-purple-100 text-xl">
              {t('sections.gdprRights.object.title')}
            </h3>
            <p className="mb-4 text-purple-200/80">{t('sections.gdprRights.object.body')}</p>
          </div>

          <div className="mb-6" id="right-to-restrict">
            <h3 className="mb-3 font-body font-semibold text-purple-100 text-xl">
              {t('sections.gdprRights.restrict.title')}
            </h3>
            <p className="mb-4 text-purple-200/80">{t('sections.gdprRights.restrict.body')}</p>
          </div>

          <div className="mb-6" id="rights-related-to-automated-decision-making">
            <h3 className="mb-3 font-body font-semibold text-purple-100 text-xl">
              {t('sections.gdprRights.automated.title')}
            </h3>
            <p className="mb-4 text-purple-200/80">{t('sections.gdprRights.automated.body')}</p>
          </div>

          <div className="mb-6">
            <h3 className="mb-3 font-body font-semibold text-purple-100 text-xl">
              {t('sections.gdprRights.exercise.title')}
            </h3>
            <p className="mb-4 text-purple-200/80">{t('sections.gdprRights.exercise.body')}</p>
          </div>
        </section>

        <section id="international-data-transfers">
          <h2 className="mb-4 font-display font-semibold text-2xl text-white">
            {t('sections.internationalTransfers.title')}
          </h2>
          <p className="mb-4 text-purple-200/80">{t('sections.internationalTransfers.intro')}</p>
          <ul className="mb-4 ml-6 list-disc space-y-2 text-purple-200/80">
            {transfersItems.map((item) => (
              <li key={item.label}>
                <strong>{item.label}</strong> {item.body}
              </li>
            ))}
          </ul>
          <p className="mb-4 text-purple-200/80">{t('sections.internationalTransfers.note')}</p>
        </section>

        <section id="data-protection-officer">
          <h2 className="mb-4 font-display font-semibold text-2xl text-white">
            {t('sections.dataProtectionOfficer.title')}
          </h2>
          <p className="mb-4 text-purple-200/80">{t('sections.dataProtectionOfficer.intro')}</p>
          <ul className="mb-4 ml-6 list-disc space-y-2 text-purple-200/80">
            {dpoItems.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
          <p className="mb-4 text-purple-200/80">{t('sections.dataProtectionOfficer.contact')}</p>
        </section>

        <section id="childrens-privacy">
          <h2 className="mb-4 font-display font-semibold text-2xl text-white">
            {t('sections.childrensPrivacy.title')}
          </h2>
          <p className="mb-4 text-purple-200/80">{t('sections.childrensPrivacy.intro')}</p>

          <div className="mb-6" id="age-verification">
            <h3 className="mb-3 font-body font-semibold text-purple-100 text-xl">
              {t('sections.childrensPrivacy.ageVerification.title')}
            </h3>
            <p className="mb-4 text-purple-200/80">
              {t('sections.childrensPrivacy.ageVerification.intro')}
            </p>
            <ul className="mb-4 ml-6 list-disc space-y-2 text-purple-200/80">
              {ageVerificationItems.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
            <p className="mb-4 text-purple-200/80">
              {t('sections.childrensPrivacy.ageVerification.note')}
            </p>
          </div>

          <div className="mb-6" id="parental-consent">
            <h3 className="mb-3 font-body font-semibold text-purple-100 text-xl">
              {t('sections.childrensPrivacy.parentalConsent.title')}
            </h3>
            <p className="mb-4 text-purple-200/80">
              {t('sections.childrensPrivacy.parentalConsent.intro')}
            </p>
            <ul className="mb-4 ml-6 list-disc space-y-2 text-purple-200/80">
              {parentalConsentItems.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
            <p className="mb-4 text-purple-200/80">
              {t('sections.childrensPrivacy.parentalConsent.contact')}
            </p>
          </div>
        </section>

        <section id="data-breach-notification">
          <h2 className="mb-4 font-display font-semibold text-2xl text-white">
            {t('sections.dataBreach.title')}
          </h2>
          <p className="mb-4 text-purple-200/80">{t('sections.dataBreach.intro')}</p>
          <p className="mb-4 text-purple-200/80">{t('sections.dataBreach.listIntro')}</p>
          <ul className="mb-4 ml-6 list-disc space-y-2 text-purple-200/80">
            {breachItems.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </section>

        <section id="eu-representative">
          <h2 className="mb-4 font-display font-semibold text-2xl text-white">
            {t('sections.euRepresentative.title')}
          </h2>
          <p className="mb-4 text-purple-200/80">{t('sections.euRepresentative.intro')}</p>
          <p className="mb-4 text-purple-200/80">
            <strong>{t('sections.euRepresentative.contactLabel')}</strong>
            <br />
            {t('sections.euRepresentative.contactEmail')}
            <br />
            {t('sections.euRepresentative.contactAddress')}
          </p>
        </section>

        <section id="changes-to-privacy-policy">
          <h2 className="mb-4 font-display font-semibold text-2xl text-white">
            {t('sections.changes.title')}
          </h2>
          <p className="mb-4 text-purple-200/80">{t('sections.changes.body')}</p>
        </section>

        <section id="contact-us">
          <h2 className="mb-4 font-display font-semibold text-2xl text-white">
            {t('sections.contact.title')}
          </h2>
          <p className="mb-4 text-purple-200/80">{t('sections.contact.intro')}</p>
          <div className="mb-4 text-purple-200/80">
            {contactItems.map((item) => (
              <p key={item.label}>
                <strong>{item.label}</strong> {item.value}
              </p>
            ))}
          </div>

          <div className="mb-4">
            <p className="mb-2 font-medium text-purple-100">{t('sections.contact.exercise')}</p>
            <p className="text-purple-200/80">{t('sections.contact.exerciseBody')}</p>
          </div>
        </section>
      </div>
    </LegalPageLayout>
  );
}
